import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { TlaaqLogo } from '../../components/brand/TlaaqLogo'
import { LocaleSwitcher } from '../../components/locale/LocaleSwitcher'
import { Card, Icon, I } from '../../components/ui'
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport'

interface AuthLayoutProps {
  eyebrow?: string
  title: string
  subtitle?: string
  heroTitle?: string
  heroSubtitle?: string
  children: ReactNode
  footer?: ReactNode
  onHome?: () => void
}

export function AuthLayout({ eyebrow, title, subtitle, heroTitle, heroSubtitle, children, footer, onHome }: AuthLayoutProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobileViewport()
  const pillKeys: ReadonlyArray<'channels' | 'ownInventory' | 'stockSplit'> = ['channels', 'ownInventory', 'stockSplit']
  const homeButton = onHome && (
    <button
      type="button"
      onClick={onHome}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        padding: '9px 13px',
        border: '1px solid var(--border-subtle)',
        background: 'rgba(255,255,255,0.78)',
        borderRadius: 999,
        boxShadow: 'var(--shadow-sm)',
        color: 'var(--brand-700)',
        fontFamily: 'inherit',
        fontSize: 12.5,
        fontWeight: 800,
        cursor: 'pointer',
      }}
    >
      <Icon size={14} color="var(--brand-700)">{I.home}</Icon>
      {t('auth.layout.backHome')}
    </button>
  )

  return (
    <div style={{
      minHeight: '100dvh',
      background: 'linear-gradient(135deg, var(--brand-100) 0%, var(--bg) 46%, var(--accent-100) 100%)',
      display: 'grid',
      alignItems: isMobile ? 'start' : 'center',
      justifyItems: 'center',
      padding: isMobile ? '18px 16px 28px' : 28,
      position: 'relative',
      boxSizing: 'border-box',
      overflowX: 'hidden',
    }}>
      {!isMobile && (
        <div style={{ position: 'absolute', top: 20, insetInlineEnd: 24 }}>
          <LocaleSwitcher />
        </div>
      )}
      {!isMobile && homeButton && (
        <div style={{ position: 'absolute', top: 20, insetInlineStart: 24 }}>
          {homeButton}
        </div>
      )}

      <div style={{
        width: '100%',
        maxWidth: isMobile ? 440 : 1040,
        display: 'grid',
        gap: isMobile ? 14 : 28,
      }}>
        {isMobile && (
          <div style={{ display: 'flex', justifyContent: homeButton ? 'space-between' : 'flex-start', alignItems: 'center', width: '100%', gap: 12 }}>
            {homeButton}
            <LocaleSwitcher />
          </div>
        )}

        <div style={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'minmax(0, 0.9fr) minmax(360px, 440px)',
          gap: isMobile ? 18 : 28,
          alignItems: 'center',
        }}>
          <section style={{
            display: 'grid',
            gap: isMobile ? 14 : 22,
            order: isMobile ? 2 : 0,
            minWidth: 0,
            justifyItems: isMobile ? 'start' : 'stretch',
            paddingInline: isMobile ? 2 : 0,
          }}>
            <TlaaqLogo height={isMobile ? 30 : 34} />
            <div style={{ display: 'grid', gap: isMobile ? 12 : 14, maxWidth: 540 }}>
              <h1 style={{
                margin: 0,
                color: 'var(--brand-900)',
                fontSize: isMobile ? 28 : 42,
                lineHeight: isMobile ? 1.12 : 1.05,
                letterSpacing: isMobile ? '-0.03em' : '-0.04em',
                maxWidth: 520,
                textAlign: 'start',
                overflowWrap: 'break-word',
                whiteSpace: 'pre-line',
              }}>
                {heroTitle ?? t('auth.layout.hero.title')}
              </h1>
              <p style={{ margin: 0, color: 'var(--fg-muted)', fontSize: isMobile ? 14 : 17, lineHeight: isMobile ? 1.6 : 1.65, maxWidth: 540, textAlign: 'start' }}>
                {heroSubtitle ?? t('auth.layout.hero.subtitle')}
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: isMobile ? 8 : 10, width: '100%', maxWidth: 540 }}>
              {pillKeys.map(key => (
                <div key={key} style={{
                  padding: isMobile ? '10px 8px' : '11px 12px',
                  borderRadius: 12,
                  border: '1px solid var(--border-subtle)',
                  background: 'rgba(255,255,255,0.72)',
                  color: 'var(--fg-muted)',
                  fontSize: isMobile ? 12 : 12.5,
                  fontWeight: 700,
                  lineHeight: 1.35,
                  textAlign: 'center',
                  minWidth: 0,
                }}>
                  {t(`auth.layout.hero.pills.${key}`)}
                </div>
              ))}
            </div>
          </section>

          <Card padding={isMobile ? 20 : 28} style={{ boxShadow: 'var(--shadow-lift)', order: isMobile ? 1 : 0, width: '100%' }}>
            <div style={{ display: 'grid', gap: isMobile ? 20 : 22 }}>
              <div style={{ display: 'grid', gap: 7 }}>
                {eyebrow && (
                  <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--brand-700)', textAlign: 'start' }}>
                    {eyebrow}
                  </div>
                )}
                <h2 style={{ margin: 0, color: 'var(--brand-900)', fontSize: isMobile ? 26 : 28, lineHeight: 1.1, letterSpacing: '-0.03em', textAlign: 'start' }}>{title}</h2>
                {subtitle && <p style={{ margin: 0, color: 'var(--fg-muted)', fontSize: 14, lineHeight: 1.55, textAlign: 'start' }}>{subtitle}</p>}
              </div>
              {children}
              {footer && (
                <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 18 }}>
                  {footer}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
